import React, { useMemo, memo } from 'react';

import classnames from 'classnames';

import Typography from 'components/uiLibrary/Typography';

import { CALENDAR_VIEW_TYPES } from 'constants/calendar';
import { DATE_FORMATS } from 'constants/index';

import { getDayHeaders, isWeekend, getViewRange } from 'utils/calendar';
import { createDate } from 'utils/date';

import classes from './CalendarHeaders.module.scss';

const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const CalendarHeaders = memo(({ viewType, rangeStartDate, highlightDates = [] }) => {
  const days = useMemo(() => {
    if ([CALENDAR_VIEW_TYPES.MONTH, CALENDAR_VIEW_TYPES.FIFTEEN_DAYS].includes(viewType)) {
      return getDayHeaders({
        rangeStartDate,
        daysToRender: viewType === CALENDAR_VIEW_TYPES.MONTH ? 30 : 15,
      });
    }

    return [];
  }, [viewType, rangeStartDate]);

  const formattedHighlightDates = useMemo(
    () => highlightDates.map(date => createDate(date).format(DATE_FORMATS.FULL_DATE)),
    [highlightDates],
  );

  const { months } = useMemo(() => getViewRange({ viewType, rangeStartDate }) || [], [viewType, rangeStartDate]);

  if (days?.length) {
    return days.map(({ date, day, weekday }) => {
      const dayFormatted = createDate(date).format(DATE_FORMATS.FULL_DATE);
      const isSelected = formattedHighlightDates.includes(dayFormatted);
      const isWeekendDay = isWeekend(createDate(date));

      return (
        <th
          key={dayFormatted}
          className={classnames(classes.dayHeader, {
            [classes.dayHeaderWeekend]: isWeekendDay,
            [classes.dayHeaderDateSelected]: !!isSelected,
          })}
        >
          <Typography
            variant="div"
            size={viewType === CALENDAR_VIEW_TYPES.FIFTEEN_DAYS ? 12 : 10}
            weight="medium"
            className={classnames(classes.dayHeader__day, {
              [classes.dayHeader__day_fifteenDays]: viewType === CALENDAR_VIEW_TYPES.FIFTEEN_DAYS,
            })}
          >
            {day}
          </Typography>
          <Typography size={10}>{weekday}</Typography>
        </th>
      );
    });
  }

  if (viewType === CALENDAR_VIEW_TYPES.YEAR) {
    return months?.map((month, index) => (
      <th
        key={index}
        className={classnames(classes.yearMonthsHeader, classes.yearMonthsHeaderWithDots, {
          [classes.yearMonthsHeaderLast]: month === 'Dec' && index !== months.length - 1,
        })}
      >
        <Typography variant="div" size={10} weight="medium" className={classes.monthName}>
          {month}
        </Typography>
        <div className={classes.weekdaysContainer}>
          {weekdays.map((weekday, dayIndex) => (
            <Typography key={dayIndex} className={classes.weekdayLabel}>
              {weekday}
            </Typography>
          ))}
        </div>
      </th>
    ));
  }

  if (viewType === CALENDAR_VIEW_TYPES.QUARTER) {
    return months?.map((month, index) => {
      const currentDate = rangeStartDate.add(index, 'month');
      const currentYear = currentDate.year();
      const monthNumber = currentDate.month() + 1;
      const lastDay = new Date(currentYear, monthNumber, 0).getDate();
      const monthDays = Array.from({ length: lastDay }, (_, i) => i + 1);

      return (
        <th key={index} className={classnames(classes.yearMonthsHeader, classes.quarterMonthsHeaderWithBars)}>
          <Typography variant="div" size={10} weight="medium" className={classes.monthName}>
            {month}
          </Typography>
          <div className={classes.dayBarsContainer}>
            {monthDays.map(day => {
              const date = createDate(
                `${currentYear}-${monthNumber.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`,
              );
              const isWeekendDay = isWeekend(date);
              const dateFormatted = date.format(DATE_FORMATS.FULL_DATE);
              const dayOfWeek = date.day(); // NOTE: 0-6 (Sunday=0)
              const weekdayLetter = weekdays[dayOfWeek];

              const isHighlighted = highlightDates.includes(dateFormatted);

              return (
                <div
                  key={`${currentYear}-${monthNumber}-${day}`}
                  className={classnames(classes.weekdayCell, {
                    [classes.weekdayHighlighted]: isHighlighted,
                    [classes.weekdayWeekend]: isWeekendDay,
                  })}
                >
                  <Typography className={classes.weekdayLabel}>{weekdayLetter}</Typography>
                </div>
              );
            })}
          </div>
        </th>
      );
    });
  }

  return months?.map((month, index) => (
    <th
      key={index}
      className={classnames(classes.yearMonthsHeader, {
        [classes.quarterMonthHeader]: viewType === CALENDAR_VIEW_TYPES.QUARTER,
      })}
    >
      {month}
    </th>
  ));
});

export default CalendarHeaders;
